import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { COLOR, FONT, RADIUS, SPACE, Z } from "../lib/tokens";

// InviteTeammatesSheet — mid-cook invite. Lists family + friends with a
// per-person mode toggle (HELPER / DINER / off). Confirm fires
// inviteParticipants on useTeamCookSession.
//
// Props:
//   open
//   onClose
//   family, friends   — arrays of useRelationships rows (otherId + other.name)
//   selfUserId        — current user (hidden from list)
//   existingIds       — Set<user_id> already in cook_session_participants
//   onInvite          — async ({ helperIds, dinerIds }) → null

const MODES = [
  { id: "off",    label: "off",    bg: COLOR.deep,   fg: COLOR.muted },
  { id: "helper", label: "COOK",   bg: COLOR.leaf,   fg: "#fff"      },
  { id: "diner",  label: "EATER",  bg: COLOR.gold,   fg: "#1E1E1E"   },
];

export default function InviteTeammatesSheet({
  open,
  onClose,
  family = [],
  friends = [],
  selfUserId,
  existingIds,
  onInvite,
}) {
  const [picks, setPicks] = useState(() => new Map()); // user_id → 'helper'|'diner'
  const [busy, setBusy] = useState(false);

  const teammates = useMemo(() => {
    const seen = new Set();
    const out = [];
    const add = (r, isFamily) => {
      if (!r?.otherId || !r?.other?.name) return;
      if (r.otherId === selfUserId) return;
      if (existingIds?.has(r.otherId)) return;
      if (seen.has(r.otherId)) return;
      seen.add(r.otherId);
      out.push({
        id: r.otherId,
        name: r.other.name.trim().split(/\s+/)[0],
        isFamily,
      });
    };
    for (const f of family)  add(f, true);
    for (const f of friends) add(f, false);
    return out;
  }, [family, friends, selfUserId, existingIds]);

  if (!open) return null;

  const cycle = (uid) => {
    setPicks((prev) => {
      const next = new Map(prev);
      const cur = next.get(uid);
      if (!cur) next.set(uid, "helper");
      else if (cur === "helper") next.set(uid, "diner");
      else next.delete(uid);
      return next;
    });
  };

  const count = picks.size;
  const confirm = async () => {
    if (!onInvite || count === 0) return;
    setBusy(true);
    try {
      const helperIds = [];
      const dinerIds = [];
      for (const [uid, role] of picks.entries()) {
        if (role === "helper") helperIds.push(uid);
        else if (role === "diner") dinerIds.push(uid);
      }
      await onInvite({ helperIds, dinerIds });
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  // Portal to body so position:fixed escapes any transformed ancestor
  // in CookMode (motion.div wrappers break fixed-positioning otherwise).
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,20,20,0.45)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: Z.sheet,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: COLOR.night,
          borderTopLeftRadius: RADIUS.sheet,
          borderTopRightRadius: RADIUS.sheet,
          padding: `${SPACE.section}px ${SPACE.section}px calc(${SPACE.hero}px + env(safe-area-inset-bottom))`,
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <div style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 1.2,
          color: COLOR.muted,
          marginBottom: SPACE.xs,
        }}>
          INVITE TO THIS COOK
        </div>
        <div style={{
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontSize: 22,
          color: COLOR.ink,
          marginBottom: SPACE.md,
        }}>
          Bring people in
        </div>
        <div style={{
          fontFamily: FONT.sans,
          fontSize: 12,
          color: COLOR.muted,
          marginBottom: SPACE.section,
          lineHeight: 1.5,
        }}>
          Tap to add as COOK (can be assigned steps) or tap again for
          EATER (watching only). Tap a third time to remove.
        </div>

        {teammates.length === 0 ? (
          <div style={{
            fontFamily: FONT.sans,
            fontSize: 13,
            color: COLOR.muted,
            padding: `${SPACE.lg}px 0`,
          }}>
            No teammates to invite. Add family or friends in Settings first.
          </div>
        ) : (
          <ul style={{
            listStyle: "none",
            padding: 0,
            margin: `0 0 ${SPACE.section}px`,
            display: "flex",
            flexDirection: "column",
            gap: SPACE.xs,
          }}>
            {teammates.map((t) => {
              const mode = picks.get(t.id) || "off";
              const modeRec = MODES.find((m) => m.id === mode);
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    className="mcm-focusable"
                    onClick={() => cycle(t.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: SPACE.md,
                      width: "100%",
                      padding: `${SPACE.md}px ${SPACE.lg}px`,
                      background: COLOR.ground,
                      border: `1px solid ${mode === "off" ? COLOR.border : modeRec.bg}`,
                      borderRadius: RADIUS.md,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span style={{
                      flex: 1,
                      fontFamily: FONT.forma,
                      fontSize: 14,
                      fontWeight: 600,
                      color: COLOR.ink,
                    }}>
                      {t.name}
                      {t.isFamily ? null : (
                        <span style={{
                          fontFamily: FONT.mono,
                          fontSize: 9,
                          color: COLOR.muted,
                          marginLeft: 6,
                          letterSpacing: 1,
                        }}>FRIEND</span>
                      )}
                    </span>
                    <span style={{
                      fontFamily: FONT.mono,
                      fontSize: 10,
                      letterSpacing: 1.2,
                      padding: "4px 10px",
                      background: modeRec.bg,
                      color: modeRec.fg,
                      borderRadius: 999,
                      minWidth: 56,
                      textAlign: "center",
                    }}>
                      {modeRec.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div style={{ display: "flex", gap: SPACE.md }}>
          <button
            type="button"
            className="mcm-focusable"
            onClick={onClose}
            style={{
              flex: 1,
              fontFamily: FONT.mono,
              fontSize: 11,
              letterSpacing: 1.4,
              padding: `${SPACE.md}px 0`,
              background: COLOR.ground,
              color: COLOR.ink,
              border: `1px solid ${COLOR.border}`,
              borderRadius: RADIUS.md,
              cursor: "pointer",
            }}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="mcm-focusable"
            onClick={confirm}
            disabled={busy || count === 0}
            style={{
              flex: 2,
              fontFamily: FONT.mono,
              fontSize: 11,
              letterSpacing: 1.4,
              padding: `${SPACE.md}px 0`,
              background: count === 0 ? COLOR.deep : COLOR.leaf,
              color: count === 0 ? COLOR.muted : "#fff",
              border: "none",
              borderRadius: RADIUS.md,
              cursor: count === 0 ? "default" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy
              ? "SENDING…"
              : count === 0
                ? "PICK SOMEONE"
                : `INVITE ${count}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
